import clsx from 'clsx';
import { EqualIcon } from 'lucide-react';
import { useIntl } from 'react-intl';

import InformationIcon from '@/assets/icons/information.svg';
import { CloseIcon } from '@/components/icons/close';
import { MinusIcon } from '@/components/icons/minus';
import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useFormattedDays } from '@/hooks/use-formatted-days';

export function Calculation({
  hasHiringGoal = false,
}: {
  hasHiringGoal?: boolean;
}) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { hookForm, hiringTarget } = useEditCampaignContext();
  const formatDaysByBudgetType = useFormattedDays();
  const { getCalculationBudgetLabel } = useBudgetType();

  const { targetBudget, projectedBudget, overlapDuration, preHiringGoalSpend } =
    hiringTarget;

  const { watch } = hookForm;
  const inputBudget = watch('budget') || 0;
  const budgetType = watch('budget_type');

  const spendTitle = getCalculationBudgetLabel(budgetType);
  const isLifetimeBudget = budgetType === 'lifetime' || !budgetType;

  // Hiring target for lifetime budget is the input budget minus the pre-hiring goal spend
  if (isLifetimeBudget)
    return (
      <>
        <ReturnBackArrowIcon />

        <Metric label={spendTitle} value={formatCurrency(inputBudget)} />

        <MinusIcon className="text-fx-primary mt-0.5" />

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Spent before hiring goal',
            description:
              'Budget math row: ad spend that occurred before the current goal',
          })}
          value={formatCurrency(preHiringGoalSpend)}
        />

        <EqualIcon className="text-fx-primary mt-0.5 h-4 w-4" />

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Target budget',
            description:
              'Budget math row: what remains to allocate toward the goal',
          })}
          value={formatCurrency(targetBudget)}
        />
      </>
    );

  // Hiring target for non-lifetime budget is the per day budget multiplied by the overlap duration plus the budget spent
  return (
    <>
      <ReturnBackArrowIcon />

      <Metric label={spendTitle} value={formatCurrency(inputBudget)} />

      <CloseIcon className="text-fx-primary mt-0.5" />

      <span className="flex min-w-0 items-start gap-1">
        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Duration',
            description:
              'Non-lifetime budget: overlap days the rate applies to',
          })}
          value={formatDaysByBudgetType(overlapDuration(), budgetType)}
          info={
            hasHiringGoal && (
              <Tooltip
                content={intl.formatMessage({
                  defaultMessage:
                    'Target duration is the duration of the campaign that overlaps with the active hiring goal.',
                  description:
                    'Info tooltip: explains how “duration” is computed for the formula',
                })}
              >
                <img src={InformationIcon} alt="information" />
              </Tooltip>
            )
          }
        />
      </span>

      <EqualIcon className="text-fx-primary mt-0.5 h-4 w-4" />

      <Metric
        label={intl.formatMessage({
          defaultMessage: 'Projected spend',
          description:
            'Formula result: expected total spend over the shown duration',
        })}
        value={formatCurrency(projectedBudget)}
      />
    </>
  );
}

function Metric({
  label,
  value,
  className,
  info,
}: {
  label: string;
  value: string;
  className?: string;
  info?: React.ReactNode;
}) {
  return (
    <div className={clsx('flex min-w-0 flex-col gap-1', className)}>
      <div className="flex min-w-0 items-center gap-1">
        <Typography variant="subtitle2" className="min-w-0 break-words">
          {label}
        </Typography>
        {info}
      </div>
      <Typography variant="body1" className="whitespace-nowrap">
        {value}
      </Typography>
    </div>
  );
}
